// src/components/payments/useHostedFieldFocus.ts
//
// Which hosted card field currently has focus — tracked in the PARENT document.
//
// ============================================================================
// WHY NOT `:focus-within`
// ============================================================================
//
// Because it does not work, and the staging diagnostic is what proved it.
//
// The assumption was that an `<iframe>` whose document has focus matches
// `:focus`, so an ancestor matches `:focus-within`. Measured in Chromium with
// a real pointer click, that is false:
//
//     activeElement          -> IFRAME      (the browser DOES tell us)
//     iframe.matches(':focus')  -> false
//     shell.matches(':focus-within') -> false
//
// It is false for SAME-ORIGIN frames too, so this is not a cross-origin
// restriction — `:focus` simply does not cross a document boundary. Any CSS-
// only focus treatment on the shell is therefore unreachable, and the field
// showed no focus indication at all on staging.
//
// `document.activeElement` DOES become the iframe element, and reading it is
// ordinary parent-document DOM. So the shell's treatment is driven from here
// instead of from CSS.
//
// ============================================================================
// WHAT THIS DOES NOT DO
// ============================================================================
//
// It never touches `contentDocument`, `contentWindow` or `postMessage`, reads
// no field value, and learns nothing about what the customer typed. It reads
// one thing: which of OUR OWN elements the browser says is focused.

import { useEffect, useState } from 'react';

/** Marks a shell as a hosted-field mount point and names the field. */
export const HOSTED_FIELD_ATTR = 'data-hosted-field';

/**
 * Polling, deliberately.
 *
 * `window.blur` fires when focus first enters a frame, but moving from one
 * hosted field to the next fires NOTHING in the parent: the top window is
 * already blurred, and the frames are another origin's. Without a poll the
 * ring would stick to whichever field was entered first — worse than no ring,
 * because it would point at the wrong box.
 */
const POLL_MS = 200;

export function useHostedFieldFocus(): string | null {
  const [focused, setFocused] = useState<string | null>(null);

  useEffect(() => {
    const read = (): void => {
      const active = document.activeElement;
      if (active instanceof HTMLIFrameElement) {
        const shell = active.closest(`[${HOSTED_FIELD_ATTR}]`);
        setFocused(shell?.getAttribute(HOSTED_FIELD_ATTR) ?? null);
        return;
      }
      setFocused(null);
    };

    read();
    // Events catch the common transitions immediately; the poll catches the
    // frame-to-frame ones that produce no event at all.
    window.addEventListener('blur', read);
    window.addEventListener('focus', read);
    document.addEventListener('focusin', read);
    const timer = window.setInterval(read, POLL_MS);

    return () => {
      window.removeEventListener('blur', read);
      window.removeEventListener('focus', read);
      document.removeEventListener('focusin', read);
      window.clearInterval(timer);
    };
  }, []);

  return focused;
}
